import React, { useCallback, useEffect, useState } from "react";
import { Check, Circle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "../../lib/siteInfo";
import { LaunchCard, StatusBadge, inputClass, inputStyle, toLocalInput } from "./ui";

const PUBLISH_STEPS = [
  "In the hosting platform, open the dev-portal project and copy the tested version to a new build.",
  "Open that new build and choose Publish → Replace, then pick the existing production site.",
  "Replace keeps production's database, secrets and custom domain; only the code changes.",
  "Wait for the publish to finish (a few minutes), then come back here.",
];

function StartForm({ suggestedVersion, onStarted }) {
  const soon = new Date(Date.now() + 30 * 60000);
  const [form, setForm] = useState({
    starts_at: toLocalInput(soon.toISOString()),
    ends_at: toLocalInput(new Date(soon.getTime() + 30 * 60000).toISOString()),
    target_version: suggestedVersion || "",
    notice: "The portal is being updated. It will be unavailable for a few minutes; please sign in again afterwards.",
  });
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const start = async () => {
    setBusy(true);
    try {
      await api.post("/admin/release/runs", {
        ...form, starts_at: new Date(form.starts_at).toISOString(), ends_at: new Date(form.ends_at).toISOString(),
      });
      toast.success("Release window scheduled; clients see the usual notice");
      onStarted();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid gap-3 md:grid-cols-4" data-testid="release-run-form">
      <div><label className="field-label">Window starts</label>
        <input type="datetime-local" className={inputClass} style={inputStyle} value={form.starts_at} onChange={set("starts_at")} data-testid="release-run-start-input" /></div>
      <div><label className="field-label">Window ends</label>
        <input type="datetime-local" className={inputClass} style={inputStyle} value={form.ends_at} onChange={set("ends_at")} data-testid="release-run-end-input" /></div>
      <div><label className="field-label">Version being released</label>
        <input className={inputClass} style={inputStyle} value={form.target_version} onChange={set("target_version")} placeholder={suggestedVersion || "e.g. 2026.11.2"} data-testid="release-run-version-input" /></div>
      <div className="flex items-end"><button type="button" className="btn-primary w-full" disabled={busy} onClick={start} data-testid="release-run-start-btn">{busy ? "Scheduling…" : "1 · Schedule window"}</button></div>
      <div className="md:col-span-4"><label className="field-label">Client notice</label>
        <input className={inputClass} style={inputStyle} value={form.notice} onChange={set("notice")} data-testid="release-run-notice-input" /></div>
    </div>
  );
}

function Step({ index, step, runId, onChanged }) {
  const ack = async () => {
    try { await api.post(`/admin/release/runs/${runId}/ack`, { step: step.key }); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <li className="flex gap-3 border-b py-3 last:border-b-0" style={{ borderColor: "var(--border)" }} data-testid={`release-step-${step.key}`}>
      <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center text-xs font-bold" style={{ background: step.done ? "var(--ink)" : "transparent", color: step.done ? "var(--surface)" : "var(--ink)", border: "1px solid var(--border-strong)" }}>
        {step.done ? <Check size={13} /> : index + 1}
      </span>
      <div className="flex-1 text-sm">
        <div className="font-semibold" style={{ color: "var(--ink)" }}>{step.label}</div>
        <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }} data-testid={`release-step-detail-${step.key}`}>{step.detail || (step.done ? "Done" : "Waiting")}</div>
        {step.key === "published" && !step.done && (
          <ol className="mt-2 list-decimal pl-5 text-xs space-y-0.5" style={{ color: "var(--muted)" }} data-testid="release-publish-instructions">
            {PUBLISH_STEPS.map((line) => <li key={line}>{line}</li>)}
          </ol>
        )}
      </div>
      {step.manual && !step.done && (
        <button type="button" className="btn-ghost !py-1 !px-3 text-xs self-start" onClick={ack} data-testid={`release-step-ack-${step.key}`}>Mark done</button>
      )}
    </li>
  );
}

function ActiveRun({ run, onChanged }) {
  const act = async (verb) => {
    if (verb === "cancel" && !window.confirm("Cancel this release and end the maintenance window?")) return;
    if (verb === "close" && !window.confirm("Close maintenance now and log this release as complete?")) return;
    try { await api.post(`/admin/release/runs/${run.id}/${verb}`); toast.success(verb === "close" ? "Release completed and logged" : "Release cancelled"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="release-run-active">
      <div className="text-xs mb-2" style={{ color: "var(--muted)" }}>
        From v{run.from_version}{run.target_version ? ` to v${run.target_version}` : ""} · started by {run.created_by}
      </div>
      <ol>{run.steps.map((step, i) => <Step key={step.key} index={i} step={step} runId={run.id} onChanged={onChanged} />)}</ol>
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" className="btn-primary" onClick={() => act("close")} data-testid="release-run-close-btn">6 · Close maintenance and finish</button>
        <button type="button" className="btn-ghost" onClick={() => act("cancel")} data-testid="release-run-cancel-btn">Cancel release</button>
      </div>
    </div>
  );
}

export function ReleaseChecklist({ suggestedVersion, refreshKey }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/release/runs").then(({ data: d }) => setData(d))
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const open = Boolean(data?.active);
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [open, load]);
  if (!data) return null;
  return (
    <LaunchCard eyebrow={`Release · ${data.environment}`} title="Promote a release to this site" testId="release-checklist"
      aside={data.active ? <StatusBadge status="warn" label={data.active.status === "in_window" ? "In window" : "Scheduled"} testId="release-run-status" /> : null}>
      {data.environment !== "production" && (
        <p className="text-xs mb-3" style={{ color: "var(--muted)" }} data-testid="release-checklist-dev-note">This checklist is meant for the production site; on dev-portal it can be used for a rehearsal.</p>
      )}
      {data.active ? <ActiveRun run={data.active} onChanged={load} /> : <StartForm suggestedVersion={suggestedVersion} onStarted={load} />}
      {Boolean(data.history.length) && (
        <div className="mt-6">
          <div className="eyebrow mb-2">Completed releases</div>
          <ul className="text-xs space-y-1" style={{ color: "var(--muted)" }} data-testid="release-run-history">
            {data.history.map((r) => (
              <li key={r.id} className="flex items-center gap-2"><Circle size={6} />
                {r.status === "completed" ? `v${r.confirmed_version}` : "Cancelled"} · {formatWhen(r.closed_at)} · {r.closed_by}
                {r.backup_filename ? ` · backup ${r.backup_filename}` : ""}{r.switches.length ? ` · ${r.switches.join(", ")}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </LaunchCard>
  );
}
